import React, { useEffect, useState } from 'react';
import { Stack, Box, Button, useTheme } from '@mui/material';
import { useProduct } from '@features/products';
import { useParams } from 'react-router-dom';
import TimeTravelGraph from './components/Graph/TimeTravelGraph';
import TimeTravelTrend from './components/ForecastComponents/TimeTravelTrend';
import TimeTravelSearchTermComponent from './components/ForecastComponents/TimeTravelSearchTermComponent';
import { useForecast } from './hooks/useForecast';
import UpdateForecast from './components/UpdateForecast';
import TimeTravelSeasonality from './components/ForecastComponents/TimeTravelSeasonality';
import ForecastParameters from './components/ForecastAnalytics/ForecastParameters';
import { useProductSales } from '@features/sales/hooks/useSales';
import SalesScopeFilters, { SalesScope } from '@features/sales/components/SalesScopeFilters';
import { ForecastGradeSummary } from './components/ForecastAnalytics/ForecastGradeSummary';
import { Events } from './components/Events';
import { useImpersonation } from '@shared/hooks/useImpersonation';
import { ServiceLevel } from './components/ForecastAnalytics/ServiceLevel';
import { ForecastSourceType } from '@domain/forecast/models/Forecast';

const TimeTravelProduct: React.FC = () => {

  const [showParameters, setShowParameters] = useState(false);
  const [scope, setScope] = useState<SalesScope>({ marketplaces: [], countries: [] });

  const { productId } = useParams<{ productId: string }>();
  const marketplaces = scope.marketplaces.length > 0 ? scope.marketplaces : undefined;
  const countries = scope.countries.length > 0 ? scope.countries : undefined;
  const filtersActive = Boolean(marketplaces?.length || countries?.length);

  const { sales } = useProductSales(productId, undefined, undefined, marketplaces, countries);
  const { product } = useProduct(productId ?? '');

  const { canImpersonate } = useImpersonation();


  const { forecast } = useForecast({ sourceType: ForecastSourceType.PRODUCT, source: productId ?? '', connector: 'bol' });

  const [historicalData, setHistoricalData] = useState<{
    week: number; year: number; value: number; anomaly?: boolean; detectedAnomaly?: boolean;
  }[]>([]);

  useEffect(() => {
    if (sales?.status === 'success') {
      setHistoricalData(sales.data.weeks.map(week => ({
        week: week.week,
        year: week.year,
        value: week.sales.quantity_adjusted ?? week.sales.quantity ?? 0,
        anomaly: week.sales.anomaly,
        detectedAnomaly: week.sales.detectedAnomaly
      })));
    }
  }, [sales]);

  return (
    <Stack direction="row" spacing={2} sx={{ flex: 1, minHeight: 0 }}>

      <Stack direction="column" flex={1} spacing={4} p={0}>

        {product && forecast && (
          <Box sx={{ flexShrink: 0, position: 'sticky', top: 10, zIndex: 1000 }}>
            <UpdateForecast product={product} forecast={forecast} />
          </Box>
        )}

        <Stack direction="row" spacing={4}>

          <ForecastGradeSummary forecastAnalytics={forecast?.forecastAnalytics} />


          {sales?.status === 'success' && sales.data?.weeks && (
            <ServiceLevel weeklySales={sales.data.weeks} />
          )}
        </Stack>

        {productId && (
          <>
            <Stack direction="row" spacing={4} justifyContent="flex-end" borderTop={1} borderColor="divider" pt={2}>
              <SalesScopeFilters value={scope} onChange={setScope} />
            </Stack>

            <TimeTravelGraph
              sourceType={ForecastSourceType.PRODUCT}
              source={productId}
              connector="bol"
              historicalData={historicalData}
              hideForecast={filtersActive}
            />

            <Stack
              gap={4}
              direction={{ md: 'column', lg: 'row' }}
            >
              <TimeTravelSearchTermComponent
                productId={productId}
                connector="bol"
                canModifyParameters={true}
              />

              <Stack
                gap={4}
                flex={1}
                direction={{ xs: 'column', md: 'row' }}
              >
                <TimeTravelTrend
                  sourceType={ForecastSourceType.PRODUCT}
                  source={productId}
                  connector="bol"
                  canModifyParameters={true}
                />

                <TimeTravelSeasonality
                  sourceType={ForecastSourceType.PRODUCT}
                  source={productId}
                  connector="bol"
                  canModifyParameters={true}
                />
              </Stack>
            </Stack>

            <Events productId={productId} />
          </>
        )}

        {productId && showParameters && (
          <ForecastParameters sourceType={ForecastSourceType.PRODUCT} source={productId} connector="bol" product={product} />
        )}

        {canImpersonate && (
          <Button size="small" onClick={() => setShowParameters(!showParameters)}>{showParameters ? 'Hide Parameters' : 'Show Parameters'}</Button>
        )}
      </Stack>

    </Stack>
  );
};

export default TimeTravelProduct; 